// AssertionSummaryPanel — assertion results with expandable type rows,
// click-to-navigate, tier filtering, and grouped failure details.

import { Component, useState, useCallback, type ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectEffectiveAssertionSummary,
  selectEffectiveAssertionResults,
  selectAssertionResultsByType,
  selectSimulationStatus,
  selectFailuresByNode,
  selectActualPath,
  setActiveEventIndex,
  parseStepIndex,
  type AssertionResult,
} from '../../store/slices/simulationSlice.ts';
import {
  selectShowAssertionOverlay,
  selectExpandedAssertionTypes,
  selectAssertionTierFilter,
  toggleAssertionOverlay,
  toggleAssertionTypeExpand,
  toggleAssertionTierFilter,
  clearAssertionTierFilter,
  selectNode,
} from '../../store/slices/uiSlice.ts';
import { selectNodeById, selectConnectionsForNode } from '../../store/slices/topologySlice.ts';
import type { RootState } from '../../store/index.ts';
import { buildReport, downloadReportAsJSON } from '../../services/AssertionReportService.ts';
import { selectScenarioId, selectScenarioLabel } from '../../store/slices/eventsSlice.ts';
import { selectSuiteResults } from '../../store/slices/simulationSlice.ts';

// ---------------------------------------------------------------------------
// ErrorBoundary — prevents a crash from taking down the sidebar
// ---------------------------------------------------------------------------

class SafeBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Navigate helper — shared by all clickable assertion items
// ---------------------------------------------------------------------------

function useNavigateToAssertion() {
  const dispatch = useDispatch();
  const actualPath = useSelector(selectActualPath);

  return useCallback(
    (assertionId: string) => {
      let stepIndex = parseStepIndex(assertionId);
      // For non-step assertions or out-of-bounds steps, navigate to the last step
      if (stepIndex === null || stepIndex >= actualPath.length) {
        stepIndex = actualPath.length > 0 ? actualPath.length - 1 : null;
      }
      if (stepIndex === null) return;
      const nodeId = actualPath[stepIndex];
      if (!nodeId) return;
      dispatch(selectNode(nodeId));
      dispatch(setActiveEventIndex(stepIndex));
    },
    [dispatch, actualPath],
  );
}

// ---------------------------------------------------------------------------
// AssertionDetailCard — single assertion result with click-to-navigate
// ---------------------------------------------------------------------------

function AssertionDetailCard({
  result,
  onNavigate,
}: {
  result: AssertionResult;
  onNavigate: (assertionId: string) => void;
}) {
  const isSkipped = (result as { skipped?: boolean }).skipped === true;
  return (
    <button
      className={`assertion-detail-card ${
        isSkipped
          ? 'assertion-detail-skipped'
          : result.passed
            ? 'assertion-detail-pass'
            : 'assertion-detail-fail'
      }`}
      onClick={() => onNavigate(result.assertionId)}
      title="Click to navigate to this step"
    >
      <span className="assertion-detail-icon">{isSkipped ? '—' : result.passed ? '✓' : '✗'}</span>
      <span className="assertion-detail-text">{result.detail}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tier filter bar
// ---------------------------------------------------------------------------

const TIER_ORDER = [
  'path',
  'boundary_crossing',
  'schema_contract',
  'branch_evaluation',
  'terminal_state',
  'saga_transition',
  'saga_compensation',
  'policy_chain',
];
const TIER_LABELS: Record<string, string> = {
  path: 'path',
  boundary_crossing: 'boundary',
  schema_contract: 'schema',
  branch_evaluation: 'branch',
  terminal_state: 'terminal',
  saga_transition: 'saga',
  saga_compensation: 'compensation',
  policy_chain: 'policy',
};

function TierFilterBar({
  availableTiers,
  activeTiers,
}: {
  availableTiers: string[];
  activeTiers: string[] | null;
}) {
  const dispatch = useDispatch();
  const sorted = TIER_ORDER.filter((t) => availableTiers.includes(t));

  return (
    <div className="assertion-tier-filter">
      <button
        className={`assertion-tier-btn ${activeTiers === null ? 'assertion-tier-btn-active' : ''}`}
        onClick={() => dispatch(clearAssertionTierFilter())}
      >
        all
      </button>
      {sorted.map((tier) => (
        <button
          key={tier}
          className={`assertion-tier-btn ${activeTiers === null || activeTiers.includes(tier) ? 'assertion-tier-btn-active' : ''}`}
          onClick={() => dispatch(toggleAssertionTierFilter(tier))}
        >
          {TIER_LABELS[tier] ?? tier}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expandable type row — shows all assertions of a given type
// ---------------------------------------------------------------------------

function AssertionTypeRow({
  type,
  results,
  isExpanded,
}: {
  type: string;
  results: AssertionResult[];
  isExpanded: boolean;
}) {
  const dispatch = useDispatch();
  const navigateTo = useNavigateToAssertion();

  // Skip records (feature-detected) count as neither passed nor failed.
  const skipped = results.filter((r) => (r as { skipped?: boolean }).skipped === true).length;
  const evaluated = results.length - skipped;
  const passed = results.filter(
    (r) => r.passed && (r as { skipped?: boolean }).skipped !== true,
  ).length;
  const failed = evaluated - passed;

  return (
    <div className="assertion-type-group">
      <button
        className="assertion-type-row assertion-type-expandable"
        onClick={() => dispatch(toggleAssertionTypeExpand(type))}
      >
        <span className="assertion-type-chevron">{isExpanded ? '▼' : '▶'}</span>
        <span className="assertion-type-label">{TIER_LABELS[type] ?? type}</span>
        {evaluated === 0 && skipped > 0 ? (
          <span className="assertion-type-skipped">skipped</span>
        ) : (
          <>
            <span className="assertion-type-passed">{passed}✓</span>
            <span className="assertion-type-failed">{failed}✗</span>
            {skipped > 0 && <span className="assertion-type-skipped">{skipped} skipped</span>}
          </>
        )}
      </button>

      {isExpanded && (
        <div className="assertion-type-details">
          {results.map((r) => (
            <AssertionDetailCard key={r.assertionId} result={r} onNavigate={navigateTo} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Failure context card — structured display for a single assertion failure
// ---------------------------------------------------------------------------

function FailureCard({
  failure,
  onNavigate,
}: {
  failure: AssertionResult;
  onNavigate: (assertionId: string) => void;
}) {
  const [diagOpen, setDiagOpen] = useState(false);
  const parts = failure.assertionId.split('-');
  let contractId: string | null = null;
  let fieldName: string | null = null;

  if (failure.assertionType === 'schema_contract' && parts.length >= 4) {
    const fieldIdx = parts.indexOf('field');
    if (fieldIdx > 2) {
      contractId = parts.slice(2, fieldIdx).join('-');
      fieldName = parts.slice(fieldIdx + 1).join('-');
    }
  }

  const diag = failure.diagnosis;

  return (
    <div className="assertion-failure-card-wrapper">
      <button
        className="assertion-failure-card"
        onClick={() => onNavigate(failure.assertionId)}
        title="Click to navigate to this step"
      >
        <div className="failure-card-header">
          <span className="failure-type-badge">{failure.assertionType}</span>
          {fieldName && <span className="failure-field-name">{fieldName}</span>}
        </div>
        <div className="failure-detail">{failure.detail}</div>
        {contractId && <div className="assertion-contract-ref">Contract: {contractId}</div>}
      </button>
      {diag && (
        <>
          <button className="diagnosis-toggle" onClick={() => setDiagOpen((o) => !o)}>
            {diagOpen ? '▼' : '▶'} Why?
          </button>
          {diagOpen && (
            <div className="diagnosis-card">
              <div className="diagnosis-summary">{diag.summary}</div>
              {diag.comparison && (
                <div className="diagnosis-comparison">
                  {diag.comparison.label && (
                    <div className="diagnosis-comparison-label">{diag.comparison.label}</div>
                  )}
                  <div className="diagnosis-comparison-row">
                    <span className="diagnosis-expected">expected</span>
                    <span className="diagnosis-value">{diag.comparison.expected}</span>
                  </div>
                  <div className="diagnosis-comparison-row">
                    <span className="diagnosis-actual">actual</span>
                    <span className="diagnosis-value">{diag.comparison.actual}</span>
                  </div>
                </div>
              )}
              <div className="diagnosis-explanation">{diag.explanation}</div>
              {diag.sourceHint && (
                <div className="diagnosis-source-hint">
                  <span className="diagnosis-hint-icon">→</span>
                  {diag.sourceHint}
                </div>
              )}
              {(diag.frameName || diag.laneName) && (
                <div className="diagnosis-location">
                  {diag.frameName && <span className="diagnosis-frame">{diag.frameName}</span>}
                  {diag.laneName && <span className="diagnosis-lane">{diag.laneName}</span>}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Node failure group — collapsible group with click-to-navigate
// ---------------------------------------------------------------------------

function NodeFailureGroup({
  nodeId,
  failures,
  isExpanded,
  onToggle,
}: {
  nodeId: string;
  failures: AssertionResult[];
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const dispatch = useDispatch();
  const node = useSelector((state: RootState) => selectNodeById(state, nodeId));
  const actualPath = useSelector(selectActualPath);
  const connections = useSelector((state: RootState) => selectConnectionsForNode(state, nodeId));
  const navigateTo = useNavigateToAssertion();

  const handleNavigate = useCallback(() => {
    dispatch(selectNode(nodeId));
    const idx = actualPath.lastIndexOf(nodeId);
    dispatch(setActiveEventIndex(idx >= 0 ? idx : null));
  }, [dispatch, nodeId, actualPath]);

  const inboundContracts = connections
    .filter((c) => c.to === nodeId && c.schemaContract)
    .map((c) => c.schemaContract!.contractId);

  const nodeLabel = node?.label ?? nodeId;

  return (
    <div className="assertion-node-group">
      <button className="assertion-node-header" onClick={onToggle}>
        <span className="node-group-chevron">{isExpanded ? '▼' : '▶'}</span>
        <span
          className="node-group-label"
          onClick={(e) => {
            e.stopPropagation();
            handleNavigate();
          }}
        >
          {nodeId}
        </span>
        <span className="node-group-name">{nodeLabel}</span>
        <span className="node-group-count">
          {failures.length} failure{failures.length !== 1 ? 's' : ''}
        </span>
      </button>

      {isExpanded && (
        <div className="assertion-node-failures">
          {inboundContracts.length > 0 && (
            <div className="assertion-contract-ref">Contracts: {inboundContracts.join(', ')}</div>
          )}
          {failures.map((f) => (
            <FailureCard key={f.assertionId} failure={f} onNavigate={navigateTo} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Failure overlay — isolated component wrapped in SafeBoundary
// ---------------------------------------------------------------------------

function FailureOverlay() {
  const failuresByNode = useSelector(selectFailuresByNode);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleNode = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const nodeIds = Object.keys(failuresByNode);
  if (nodeIds.length === 0) return null;

  return (
    <div className="assertion-failures">
      <h4>Failures by Node</h4>
      {nodeIds.map((nodeId) => (
        <NodeFailureGroup
          key={nodeId}
          nodeId={nodeId}
          failures={failuresByNode[nodeId]}
          isExpanded={expandedNodes.has(nodeId)}
          onToggle={() => toggleNode(nodeId)}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export default function AssertionSummaryPanel() {
  const dispatch = useDispatch();
  const summary = useSelector(selectEffectiveAssertionSummary);
  const resultsByType = useSelector(selectAssertionResultsByType);
  const status = useSelector(selectSimulationStatus);
  const showOverlay = useSelector(selectShowAssertionOverlay);
  const expandedTypes = useSelector(selectExpandedAssertionTypes);
  const tierFilter = useSelector(selectAssertionTierFilter);
  const scenarioId = useSelector(selectScenarioId);
  const scenarioLabel = useSelector(selectScenarioLabel);
  const actualPath = useSelector(selectActualPath);
  const allResults = useSelector(selectEffectiveAssertionResults);
  const suiteResults = useSelector(selectSuiteResults);

  const handleExport = useCallback(() => {
    const report = buildReport({
      scenarioId,
      scenarioLabel,
      simulationStatus: status,
      summary,
      results: allResults,
      actualPath,
      suiteResults: suiteResults.length > 0 ? suiteResults : undefined,
    });
    downloadReportAsJSON(report);
  }, [scenarioId, scenarioLabel, status, summary, allResults, actualPath, suiteResults]);

  if (status === 'idle') return null;

  const hasFailures = summary.failed > 0;
  const availableTiers = Object.keys(resultsByType);

  // Filter types by tier filter
  const visibleTypes =
    tierFilter === null ? availableTiers : availableTiers.filter((t) => tierFilter.includes(t));

  // Compute filtered counts
  const filteredTotal =
    tierFilter === null
      ? summary.total
      : visibleTypes.reduce((sum, t) => sum + (resultsByType[t]?.length ?? 0), 0);
  const filteredPassed =
    tierFilter === null
      ? summary.passed
      : visibleTypes.reduce(
          (sum, t) => sum + (resultsByType[t]?.filter((r) => r.passed).length ?? 0),
          0,
        );
  // Skips are neither passed nor failed — never derive failures by
  // subtraction or every skip double-reports as a failure.
  const filteredFailed =
    tierFilter === null
      ? summary.failed
      : visibleTypes.reduce(
          (sum, t) =>
            sum +
            (resultsByType[t]?.filter(
              (r) => !r.passed && (r as { skipped?: boolean }).skipped !== true,
            ).length ?? 0),
          0,
        );

  return (
    <SafeBoundary>
      <div className="assertion-summary-panel">
        {/* Summary counts */}
        <div className="assertion-counts">
          <span className="assertion-total">{filteredTotal} assertions</span>
          <span className="assertion-passed">{filteredPassed} passed</span>
          {(() => {
            // Feature-detect the store's upcoming skipped count.
            const s = (summary as { skipped?: number }).skipped ?? 0;
            return s > 0 ? <span className="assertion-skipped">{s} skipped</span> : null;
          })()}
          {hasFailures ? (
            <button
              className="assertion-failed assertion-toggle-btn"
              onClick={() => dispatch(toggleAssertionOverlay())}
              title={showOverlay ? 'Hide failure details' : 'Show failure details'}
            >
              {filteredFailed} failed {showOverlay ? '▲' : '▼'}
            </button>
          ) : (
            <span className="assertion-failed">{filteredFailed} failed</span>
          )}
          <button
            className="assertion-export-btn"
            onClick={handleExport}
            title="Export assertion report as JSON"
            aria-label="Export assertion report"
          >
            Export
          </button>
        </div>

        {/* Tier filter */}
        {availableTiers.length > 1 && (
          <TierFilterBar availableTiers={availableTiers} activeTiers={tierFilter} />
        )}

        {/* Expandable type rows */}
        {visibleTypes.length > 0 && (
          <div className="assertion-by-type">
            {TIER_ORDER.filter((t) => visibleTypes.includes(t)).map((type) => (
              <AssertionTypeRow
                key={type}
                type={type}
                results={resultsByType[type]}
                isExpanded={expandedTypes.includes(type)}
              />
            ))}
          </div>
        )}

        {/* Failure overlay (grouped by node) */}
        {showOverlay && hasFailures && (
          <SafeBoundary>
            <FailureOverlay />
          </SafeBoundary>
        )}
      </div>
    </SafeBoundary>
  );
}
